import React, { ChangeEvent, useCallback, useRef } from "react";

import Button from "veneer/Button";

import {
  DEFAULT_CONTENT_DURATION,
  VALID_MIMETYPES,
} from "cortex/ui/src/fleet/constant/mediaLibrary";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { uploadUserContentToS3Action } from "cortex/ui/src/redux-store/user-contents/actions";

import { isValidMimeType } from "../utils";

interface UploadMediaPayload {
  duration: number;
  file: File;
  filename: string;
  folderId?: number;
  name: string;
}

interface MediaUploadButtonProps {
  folderId?: number;
  onError: (message: string) => void;
}

export default function MediaUploadButton({
  folderId,
  onError,
}: MediaUploadButtonProps) {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleButtonClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (!file) {
        return;
      }

      if (!isValidMimeType(file.type)) {
        onError(
          `File mime type should be one of ${VALID_MIMETYPES.join(", ")}`,
        );
        event.target.value = "";
        return;
      }

      const payload: UploadMediaPayload = {
        duration: DEFAULT_CONTENT_DURATION,
        file,
        filename: file.name,
        folderId,
        name: file.name,
      };

      dispatch(
        uploadUserContentToS3Action(
          payload as Parameters<typeof uploadUserContentToS3Action>[0],
          "Media uploaded!",
        ),
      );
      event.target.value = "";
    },
    [dispatch, folderId, onError],
  );

  return (
    <>
      <input hidden onChange={handleChange} ref={inputRef} type="file" />
      <Button onClick={handleButtonClick}>Upload Media</Button>
    </>
  );
}
